import projectsModel from '$$projectModel';
import Store from '$$globalStore';
import customDayTemplatesModel from '$$customDayTemplatesModel';
import moment from 'moment';
import baselinesModel from '../../../models/baselines';
import constants from '$$helpers/constants';
import timeParser from '../../../helpers/timeParser';
import filterConstants from '../../filter/constants';
import numbersHelper from '../../../helpers/numbers';

const MONTH_LOCALES = [
  __('report_month_jan'),
  __('report_month_feb'),
  __('report_month_march'),
  __('report_month_apr'),
  __('report_month_may'),
  __('report_month_jun'),
  __('report_month_jul'),
  __('report_month_aug'),
  __('report_month_sep'),
  __('report_month_oct'),
  __('report_month_nov'),
  __('report_month_dec'),
];

const LABEL_COLORS = [
  { id: 1, hex: '#4DB6AC' },
  { id: 2, hex: '#7986CB' },
  { id: 3, hex: '#CE93D8' },
  { id: 4, hex: '#50C7D6' },
  { id: 5, hex: '#FFAB91' },
  { id: 6, hex: '#81C784' },
  { id: 7, hex: '#EB8686' },
  { id: 8, hex: '#78909C' },
  { id: 9, hex: '#C0CA33' },
  { id: 10, hex: '#F9D06B' },
  { id: 11, hex: '#A1887F' },
];

const MODELS_OF_CALCULATIONS_MENU = [
  {
    type: 'item',
    name: constants.OVERVIEW_TYPES.ACTUAL_TO_PLANNED,
    text: __(`overview_type_${constants.OVERVIEW_TYPES.ACTUAL_TO_PLANNED}`),
    isActive: false,
    isDisabled: false,
    tooltip: { content: __(`reports_pages_calculation_model_tooltip_${constants.OVERVIEW_TYPES.ACTUAL_TO_PLANNED}_single_report`), position: 'right' },
  },
  {
    type: 'submenu',
    name: constants.OVERVIEW_TYPES.ACTUAL_TO_BASELINE,
    text: __(`overview_type_${constants.OVERVIEW_TYPES.ACTUAL_TO_BASELINE}`),
    list: [],
    isActive: false,
    isDisabled: false,
    selectedSubmenuId: null,
    tooltip: { content: __(`reports_pages_calculation_model_tooltip_${constants.OVERVIEW_TYPES.ACTUAL_TO_BASELINE}_single_report`), position: 'right' },
  },
  {
    type: 'submenu',
    name: constants.OVERVIEW_TYPES.PLANNED_TO_BASELINE,
    text: __(`overview_type_${constants.OVERVIEW_TYPES.PLANNED_TO_BASELINE}`),
    list: [],
    isActive: false,
    isDisabled: false,
    selectedSubmenuId: null,
    tooltip: { content: __(`reports_pages_calculation_model_tooltip_${constants.OVERVIEW_TYPES.PLANNED_TO_BASELINE}_single_report`), position: 'right' },
  },
];

export const ColorCycler = (() => {
  let colorCounter = 0;
  const groupColorMap = {};

  return {
    getColor(key) {
      if (!groupColorMap[key]) {
        const colors = LABEL_COLORS;

        groupColorMap[key] = colors[colorCounter % colors.length].hex;
        colorCounter++;
      }

      return groupColorMap[key];
    },
  };
})();

export default {
  prepareTaskResourcesForWorker(tasks) {
    const allResourcesData = Store.getters['resourcesModel/getAllResources'];
    const allResourcesMap = new Map();
    
    allResourcesData.forEach(resource => {
      if (!allResourcesMap.has(resource.id)) {
        allResourcesMap.set(resource.id, resource);
      }
    })
    return tasks.map(task => {
      const resources = task.resources.map(res => {
        const resourceData = allResourcesMap.get(res.resource_id);

        return {
          ...res,
          workingHours: resourceData?.workingHours,
          workingDays: resourceData?.workingDays,
        };
      });

      return {
        ...task,
        resources,
      };
    });
  },
  getCurrencyCode() {
    const currencies = GT.currency.map(({ ID, VALUE }) => ({
      id: ID,
      name: `${VALUE} ${ID}`,
      value: VALUE,
    }));

    const current = currencies.find(item => item.id === Store.getters['teamSetting/currency']);
    const enabledCurrency = Store.getters['teamSetting/enableCurrency'];
    let currencyCode = '$';

    if (current && enabledCurrency) {
      currencyCode = current.value;
    }

    return currencyCode;
  },

  getTextValue(timeValue) {
    if (timeValue) {
      const text = timeParser.output(timeValue, {
      // durationData: gantt.config.durationData,
        prop: 'estimation',
      });

      if (timeValue < 0) {
        return `- ${text}`;
      }

      return text;
    }

    if (timeValue === 0) {
      return `0${__('hour_in_time_string')}`;
    }

    return '';
  },

  getFormattedBudgetValue(value) {
    let numValue = value;

    if (Number.isFinite(+value)) {
      numValue = Math.ceil(+value * 100) / 100;
    }

    numValue = numbersHelper.getFormatted(numValue);

    return `${this.getCurrencyCode()} ${numValue}`;
  },

  getFormattedValueByReportRoute(value, route) {
    if (route.path.includes('/reports/budget')) {
      return this.getFormattedBudgetValue(value);
    }

    if (route.path.includes('/reports/time')) {
      return this.getTextValue(value);
    }

    return value;
  },

  prepareProjectConfigs() {
    const projectConfigs = [];
    const ganttIds = Store.getters['tasksModel/getAllData'].map(o => o.id);

    ganttIds.forEach(ganttId => {
      const projectConfig = projectsModel.getProjectConfig(ganttId);
      const customDaysData = customDayTemplatesModel.serialize().find(data => +data.ganttId === +ganttId);

      if (!projectConfig) {
        return;
      }

      // if (!projectsModel.getItem(ganttId)) {
      //   projectConfig = Store.getters['tasksModel/getItem'](ganttId).workloadData;
      // }

      const workDays = new Array(7).fill(false);

      projectConfig.durationData.showDay.forEach(dayIndex => {
        if (dayIndex === 7) {
          workDays[0] = true; // sunday should be first
        } else {
          workDays[dayIndex] = true;
        }
      });

      projectConfigs.push({
        calendar: {
          id: ganttId,
          worktime: {
            hours: projectConfig.durationData.showTime, // calendar.getConfig().hours,
            days: workDays, // _.values(calendar.getConfig().dates),
          },
        },
        ganttId,
        ganttConfig: {
          estimation_mode: projectConfig.estimation_mode,
          apply_resource_calendar: projectConfig.apply_resource_calendar,
        },
        customDays: customDaysData ? customDaysData.customDays : [],
        totalEstimate: Store.getters['tasksModel/getTotalEstimateDataForProject'](ganttId),
      });
    });

    return projectConfigs;
  },

  returnTaskByRange(task, range) {
    if (!range) {
      return task;
    }

    const startDate = moment(task.start_date);
    const endDate = moment(task.end_date);
    // const taskByRange = (startDate.isSameOrAfter(range.start) && endDate.isSameOrBefore(range.end))
    //     || (startDate.isBefore(range.end) && endDate.isSameOrAfter(range.start)
    //     && !(startDate.isBefore(range.start) && endDate.isAfter(range.end)));

    const taskByRange = !(endDate.isBefore(range.start) || startDate.isAfter(range.end));

    return taskByRange ? task : false;
  },

  returnTasksByFilterText(tasks, text, property) {
    const filterText = text.trim();

    if (filterText !== '') {
      const result = tasks.filter(task => {
        const taskName = property ? task[property] : task.task_name || task.task.name;

        return taskName.includes(text.trim());
      });

      return result;
    }

    return tasks;
  },

  returnTasksByFilterAssignee(tasks, filterAssignee, property = 'assignee') {
    if (Array.isArray(filterAssignee) && filterAssignee.length > 0) {
      return tasks.filter(task => {
        if (Array.isArray(task[property])) {
          if (!task[property].length && filterAssignee.includes('-1')) {
            return true;
          }

          return task[property].some(resource => filterAssignee.includes(resource.id));
        }

        if (typeof task[property] === 'object' && !Array.isArray(task[property])) {
          return filterAssignee.includes(task[property].id);
        }

        return false;
      });
    }

    return tasks;
  },

  returnTasksByCurrentFilter(tasks, currentFilter) {
    const startDate = currentFilter?.properties?.rangeDate?.start ? moment(currentFilter.properties.rangeDate.start) : null;
    const endDate = currentFilter?.properties?.rangeDate?.end ? moment(currentFilter.properties.rangeDate.end) : null;
    const filterRange = startDate && endDate ? { start: startDate, end: endDate } : null;

    return tasks.filter(task => this.returnTaskByRange({
      ...task,
      start_date: task.start_date || task.task_data.start_date,
      end_date: task.end_date || task.task_data.end_date,
    }, filterRange)).filter(task => {
      if (!currentFilter?.properties?.assignee?.length) {
        return task;
      }

      if (currentFilter.properties.assignee.includes('-1') && !task.resource_id.length) {
        return task;
      }

      const assigneeTask = task.resource_id.find(id => currentFilter.properties.assignee.includes(+id));

      if (assigneeTask) {
        return task;
      }
    }).filter(task => {
      if (!currentFilter?.properties?.text?.length) {
        return task;
      }

      const taskName = task.text || task.task_name;

      return taskName.toLowerCase().startsWith(currentFilter.properties.text.toLowerCase());
    });
  },

  getDefaultRange() {
    return {
      this_month: {
        period: 'this_month',
        start: moment().startOf('month').format('YYYY-MM-DD HH:mm:ss'),
        end: moment().endOf('month').format('YYYY-MM-DD HH:mm:ss'),
      },
      last_6_months: {
        period: 'last_6_months',
        start: moment().subtract(6, 'months').startOf('day').format('YYYY-MM-DD HH:mm:ss'),
        end: moment().endOf('day').format('YYYY-MM-DD HH:mm:ss'),
      },
    };
  },

  setToolbarRangeValue(filter) {
    const range = filter?.properties?.rangeDate ?? filter?.properties?.date;
    let rangeString = '';

    if (range?.start && range?.end) {
      const start = moment(range.start);
      const end = moment(range.end);

      rangeString = `${start.date()} ${MONTH_LOCALES[start.month()]} ${start.year()} 
        - ${end.date()} ${MONTH_LOCALES[end.month()]} ${end.year()}`;
    }

    return rangeString;
  },

  async getBaselinesByProjectId(projectId) {
    await baselinesModel.init(projectId);
    const baselines = baselinesModel.getByGanttId(projectId)
      .map(item => ({ ...item, created_at: new Date(item.created_at) }))
      .sort((a, b) => a.created_at - b.created_at);

    if (baselines.length) {
      return baselines;
    }

    return null;
  },

  async getBaselineDataByBaselineId(id, baselines) {
    const baseline = baselines.find(b => b.id === id);

    if (!baseline) {
      return null;
    }

    const data = await baselinesModel.getByGanttIdAndEventId(baseline.gantt_id, baseline.event_id);

    if (data) {
      return data;
    }

    return null;
  },

  watchChangedFilterProps(newProps, oldProps, handlersByKey) {
    Object.keys(handlersByKey).forEach(key => {
      const newVal = newProps?.[key];
      const oldVal = oldProps?.[key];
    
      if (!_.isEqual(newVal, oldVal)) {
        return handlersByKey[key](newVal, oldVal);
      }
    })
  },

  prepareActiveFilterBeforeCreateReport(filter, activeProperties, calculationModeId) {
    const sixMonthRange = [
      filterConstants.FILTERS.reportsProgressMonth,
      filterConstants.FILTERS.reportsBudgetMonth,
      filterConstants.FILTERS.reportsTimeMonth,
    ];
    const filtersWithBaselineSettings = [
      filterConstants.FILTERS.reportsBudgetProject,
      filterConstants.FILTERS.reportsTimeProject,
      filterConstants.FILTERS.reportsProgressSchedule,
      filterConstants.FILTERS.reportsBudgetResource,
      filterConstants.FILTERS.reportsTimeResource,
    ];
    const baselineCalculationModels = [constants.OVERVIEW_TYPES.ACTUAL_TO_BASELINE, constants.OVERVIEW_TYPES.PLANNED_TO_BASELINE];

    let filterProperties = {};
    const advancedSettings = {};

    if (filter?.properties) {
      const hasGanttIdsProp = 'gantt_id' in filter.properties && 'gantt_id' in activeProperties;
      const hasPortfolioIdsProp = 'portfolio_id' in filter.properties && 'portfolio_id' in activeProperties;
      const hasRangeDateProp = 'rangeDate' in filter.properties;
      const hasDateProp = 'date' in filter.properties;

      filterProperties = {
        ...filter.properties,
      };

      if (hasGanttIdsProp) {
        filterProperties = {
          ...filterProperties,
          gantt_id: activeProperties.gantt_id,
        };
      }

      if (hasPortfolioIdsProp) {
        filterProperties = {
          ...filterProperties,
          portfolio_id: activeProperties.portfolio_id,
        };
      }

      if (hasRangeDateProp) {
        const defaultRange = this.getDefaultRange();
        let range = defaultRange.this_month;

        if (sixMonthRange.includes(filter.type)) {
          range = defaultRange.last_6_months;
        }

        filterProperties = {
          ...filterProperties,
          rangeDate: { ...range },
        };
      }

      if (hasDateProp) {
        const defaultRange = this.getDefaultRange();
        const range = defaultRange.this_month;

        filterProperties = {
          ...filterProperties,
          date: { ...range },
        };
      }
    }

    if (filtersWithBaselineSettings.includes(filter.type) && baselineCalculationModels.includes(calculationModeId)) {
      advancedSettings.baselineEnabled = true;
    }

    return {
      activeProperties: filterProperties,
      advancedSettings,
    };
  },

  getModelsOfCalculationsMenu(isDisabled, calculationMode, baselines, baselineId) {
    const menu = _.cloneDeep(MODELS_OF_CALCULATIONS_MENU);

    return menu.map(item => {
      if (item.type === 'submenu') {
        return {
          ...item,
          isActive: item.name === calculationMode,
          isDisabled: !baselines?.length || isDisabled,
          list: baselines,
          selectedSubmenuId: item.name === calculationMode ? baselineId : null,
          tooltip: baselines?.length ? null : item.tooltip,
        };
      }

      return {
        ...item,
        isActive: item.name === calculationMode,
        isDisabled,
      };
    });
  },
};
